import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { API_BASE, STORAGE_KEY, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useJobs } from "@/contexts/JobsContext";
import { useAuth } from "@/contexts/AuthContext";
import { SortableTH } from "@/lib/sortableTable";
import { triggerDownload } from "@/lib/download";
import AlphabetRolodex from "@/components/realtors/AlphabetRolodex";
import EnrichAllDialog from "@/components/realtors/EnrichAllDialog";
import RealtorsToolbar from "@/components/realtors/RealtorsToolbar";
import RealtorsFilters from "@/components/realtors/RealtorsFilters";
import EmptyRealtors from "@/components/realtors/EmptyRealtors";
import ReviewQueueBanner from "@/components/realtors/ReviewQueueBanner";
import AddRealtorManuallyDialog from "@/components/realtors/AddRealtorManuallyDialog";
import MergeRealtorDialog from "@/components/realtors/MergeRealtorDialog";
import { RealtorEditDialog } from "@/components/realtors/RealtorForms";
import RealtorRow, { rowProgress } from "@/components/realtors/RealtorRow";
import RealtorMobileCard from "@/components/realtors/RealtorMobileCard";
import { useRealtorColumns } from "@/hooks/useRealtorColumns";
import useDebouncedValue from "@/hooks/useDebouncedValue";
import useServerSearchList from "@/hooks/useServerSearchList";
import useMediaQuery from "@/hooks/useMediaQuery";
import useStickyState from "@/hooks/useStickyState";
import BrokeragePeekModal from "@/components/brokerages/BrokeragePeekModal";

export default function RealtorsPage() {
  const { realtorId: focusRealtorId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // `?section=outreach` on the deep-link URL asks the profile view to
  // scroll to the outreach timeline on open. Empty string when absent.
  const scrollToSection = searchParams.get("section") || null;
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [brokerages, setBrokerages] = useState([]);
  const [locations, setLocations] = useState([]);
  // Sticky search — persists across screen navigations in the same tab
  // so users don't have to retype the same name every time they bounce
  // between Realtors ↔ Brokerages ↔ Outreach.
  const [search, setSearch] = useStickyState("realtors:search", "");
  // 300ms debounce + server-side cancel-previous fetch (newest wins) so
  // fast typing never flashes back to the full "A" list, and re-typing no
  // longer triggers the rate-limit/refresh cycle.
  const debouncedSearch = useDebouncedValue(search, 300);
  const [brokerageFilter, setBrokerageFilter] = useState("all");
  const [sort, setSort] = useState("name:asc");
  // A-Z rolodex jump. {ch, n} so re-clicking the same letter re-jumps
  // (the incrementing nonce changes the query key).
  const [letterJump, setLetterJump] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [peekBrokerageId, setPeekBrokerageId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  // Review sub-tab — either the approved main list ("main") or the
  // review queue for newly-imported realtors from open-house scans
  // ("review"). Always lands on the Main list (user request) — Review
  // is opt-in per visit.
  const [tab, setTab] = useState("main");
  const [reviewSummary, setReviewSummary] = useState({ pending: 0, recent: [] });
  const [reviewBusy, setReviewBusy] = useState(false);

  // Only mount ONE list branch (mobile cards OR desktop table) instead of
  // both hidden via CSS. Rendering ~900 realtors twice (once per branch)
  // was OOM-crashing iOS Safari mid-search — the tab died as soon as the
  // debounce fired and React had to unmount + remount 1,800 rows of nested
  // widgets. Tailwind's `md` breakpoint is 768px.
  const isMobile = !useMediaQuery("(min-width: 768px)");

  useEffect(() => {
    // Reset selection when tab changes — the two tabs have disjoint
    // realtor pools and bulk actions have different semantics on each.
    setSelected(new Set());
  }, [tab]);

  // Column definitions for the realtors table — widths persist per-user
  // via localStorage. See /app/frontend/src/hooks/useRealtorColumns.js.
  const realtorCols = useRealtorColumns();

  // Pull active jobs from the global tracker.
  const { jobs: globalJobs, activeJobs, track: trackJob } = useJobs();

  // Brokerages (filter dropdown + edit dialog) — fetched once on mount.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/brokerages");
        setBrokerages(data);
      } catch { /* non-critical */ }
    })();
  }, []);

  // Review-queue summary chip — refresh on tab switch + after reloads.
  const refreshReviewSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/realtors/review-summary");
      if (data) setReviewSummary(data);
    } catch { /* non-critical */ }
    // Nudge the sidebar nav badge to re-fetch in lockstep.
    window.dispatchEvent(new Event("realtors-review-updated"));
  }, []);

  // Clear the A-Z jump whenever the query context changes so a stale letter
  // offset can't leak into the next search/sort/filter query.
  const changeSort = useCallback((s) => { setSort(s); setLetterJump(null); }, []);
  const changeBrokerageFilter = useCallback((v) => {
    setBrokerageFilter(v);
    setLetterJump(null);
  }, []);
  useEffect(() => { setLetterJump((l) => (l ? null : l)); }, [debouncedSearch, tab]);

  // Server-powered, paginated fetch. Each new query cancels the previous
  // (AbortController inside the hook) so results snap in cleanly. Letter
  // jumps let the server compute the start offset (name:asc only).
  const fetchPage = useCallback(async ({ offset, limit, reset, signal }) => {
    const params = { offset, limit, sort };
    if (debouncedSearch) params.search = debouncedSearch;
    if (brokerageFilter && brokerageFilter !== "all") params.brokerage_id = brokerageFilter;
    if (tab === "review") params.review_status = "pending";
    if (reset && letterJump?.ch) params.letter = letterJump.ch;
    const { data } = await api.get("/realtors/paged", { params, signal });
    return { rows: data.rows || [], total: data.total || 0, offset: data.offset || 0 };
  }, [debouncedSearch, brokerageFilter, sort, tab, letterJump]);

  const depsKey = useMemo(
    () => JSON.stringify({
      s: debouncedSearch, b: brokerageFilter, sort, tab,
      lj: letterJump ? `${letterJump.ch}:${letterJump.n}` : "",
    }),
    [debouncedSearch, brokerageFilter, sort, tab, letterJump],
  );

  const {
    rows: realtors,
    setRows: setRealtors,
    total,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    reload,
  } = useServerSearchList({ fetchPage, depsKey, pageSize: 100 });

  // Rolodex only applies to the name-ascending main list (server jump).
  const showRolodex = sort === "name:asc" && !debouncedSearch && tab === "main";
  const ROLODEX_LETTERS = useMemo(
    () => ["A","B","C","D","E","F","G","H","I","J","K","L","M","N","O","P","Q","R","S","T","U","V","W","X","Y","Z","#"],
    [],
  );
  // All letters enabled in server mode — the server resolves the jump even
  // for letters not yet loaded. `firstIdByLetter` needs truthy values so
  // AlphabetRolodex renders every letter clickable.
  const rolodexFirstId = useMemo(() => {
    const m = {};
    for (const ch of ROLODEX_LETTERS) m[ch] = ch;
    return m;
  }, [ROLODEX_LETTERS]);

  const listTopRef = React.useRef(null);
  const jumpToLetter = useCallback((_id, ch) => {
    setLetterJump((p) => ({ ch, n: (p?.n || 0) + 1 }));
    requestAnimationFrame(() => {
      listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  // Infinite scroll — auto-load the next page when the sentinel scrolls
  // into view (600px lookahead so it feels seamless), with a Load-more
  // button as fallback.
  const sentinelRef = React.useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore) {
          loadMore();
        }
      },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, loadMore]);

  // Targeted in-place patch — used by PendingSocialsBadge after each accept /
  // reject so we DON'T re-run the full /realtors query.
  const patchRealtor = useCallback((patch) => {
    if (!patch?.id) return;
    setRealtors((arr) =>
      arr.map((r) => (r.id === patch.id ? { ...r, ...patch } : r)),
    );
  }, []);

  useEffect(() => {
    refreshReviewSummary();
  }, [refreshReviewSummary, tab]);

  // Locations list (admin can reassign a realtor's workspace from the edit dialog)
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/locations");
        setLocations(data.locations || []);
      } catch {
        // non-critical
      }
    })();
  }, [isAdmin]);

  // Area-driven default order for the socials column — same source as the
  // Outreach tab so a realtor's icons render in the same order in both views.
  // Scoped to the active locations (a full reload fires on location change).
  const [channelOrder, setChannelOrder] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/channel-popularity");
        if (Array.isArray(data?.order)) setChannelOrder(data.order);
      } catch {
        // non-critical — falls back to default PLATFORMS order
      }
    })();
  }, []);

  // CRM deep-link: when route is /realtors/:realtorId, fetch and open that realtor.
  useEffect(() => {
    if (!focusRealtorId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/realtors/${focusRealtorId}`);
        if (!cancelled) setEditing(data);
      } catch (e) {
        toast.error("Realtor not found", { description: formatApiError(e) });
        navigate("/realtors", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [focusRealtorId, navigate]);

  // Per-realtor enrichment state (button stays disabled while a job for
  // this realtor is in-flight).
  const enrichJobs = useMemo(() => {
    const map = {};
    for (const j of activeJobs) {
      if (j.type === "enrich_realtor" && j.target_id) {
        map[j.target_id] = j.id;
      }
    }
    return map;
  }, [activeJobs]);

  const enrichAllJob = useMemo(
    () => activeJobs.find((j) => j.type === "enrich_all") || null,
    [activeJobs],
  );

  const hasEnrichActivity = useMemo(
    () =>
      activeJobs.some((j) =>
        ["enrich_realtor", "enrich_all", "enrich_personal_websites"].includes(
          j.type,
        ),
      ),
    [activeJobs],
  );
  // Live per-realtor enrichment state; polled every 5s while any enrich job
  // is active. We only replace the ref if the payload actually differs from
  // the previous tick — otherwise a same-content poll would create a new
  // object reference and cascade re-renders through 900 memoised rows.
  const [liveProgress, setLiveProgress] = useState({});
  useEffect(() => {
    if (!hasEnrichActivity) return undefined;
    let stopped = false;
    const tick = async () => {
      // Skip polls while the tab is hidden — Chrome throttles setInterval
      // in background tabs anyway, but this stops the state churn (and
      // the "just came back to a stale banner" flash) explicitly.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      try {
        const { data } = await api.get("/realtors/enrichment-progress");
        if (stopped) return;
        const rows = data.realtors || [];
        setLiveProgress((prev) => {
          // Skip re-render when nothing changed content-wise. Cheap signature:
          // id + enrichment_status + filled-fields count + pending-fields count.
          if (rows.length === Object.keys(prev).length) {
            let identical = true;
            for (const p of rows) {
              const q = prev[p.id];
              if (
                !q ||
                q.enrichment_status !== p.enrichment_status ||
                (q.filled || []).length !== (p.filled || []).length ||
                (q.pending_fields || []).length !== (p.pending_fields || []).length
              ) {
                identical = false;
                break;
              }
            }
            if (identical) return prev;
          }
          const map = {};
          for (const p of rows) map[p.id] = p;
          return map;
        });
      } catch {
        // non-critical — keep last snapshot
      }
    };
    tick();
    // 5s cadence (was 3s) — parade banner still updates smoothly via
    // JobsContext's own 1.5s poll; this feed is only for the per-row bars.
    const iv = setInterval(tick, 5000);
    return () => {
      stopped = true;
      clearInterval(iv);
    };
  }, [hasEnrichActivity]);

  // Whenever a tracked job moves to done/failed, refresh the realtor list.
  const lastSeenStatuses = React.useRef({});
  useEffect(() => {
    let needReload = false;
    for (const [id, job] of Object.entries(globalJobs)) {
      const status = job?.status;
      if (!status) continue;
      const prev = lastSeenStatuses.current[id];
      if (prev !== status && (status === "done" || status === "failed")) {
        if (
          job.type === "enrich_realtor" ||
          job.type === "enrich_all" ||
          job.type === "enrich_personal_websites"
        ) {
          needReload = true;
        }
      }
      lastSeenStatuses.current[id] = status;
    }
    if (needReload) {
      setLiveProgress({});
      reload();
    }
  }, [globalJobs, reload]);

  // ---- Per-row progress map ----
  // CRITICAL for the 900-row list to stay responsive during enrichment.
  //
  // `rowProgress()` returns a fresh object graph (10 nested point objects)
  // every call. Passing `progress={rowProgress(r, liveProgress[r.id])}`
  // inline in the `.map()` produced a new object per row on every render
  // of this page, which the memoised <RealtorRow> saw as a changed prop
  // and re-rendered anyway. With 900 rows × 5 sub-components per row ×
  // a parent re-render every 1.5s (JobsContext poll during enrich_all),
  // the renderer chewed through memory fast enough that Chrome killed
  // the tab with "Aw Snap".
  //
  // The fix: build a `{realtorId → progressObj}` map that is
  //   a) memoised on `[realtors, liveProgress]` (so parent-only ticks
  //      from JobsContext DON'T rebuild it), AND
  //   b) preserves the prior entry reference when the derived content is
  //      unchanged (so a 5s liveProgress tick only rebuilds progress
  //      objects for the ~1 realtor being enriched right now).
  const prevProgressRef = React.useRef({});
  const progressById = useMemo(() => {
    const map = {};
    const prev = prevProgressRef.current;
    for (const r of realtors) {
      const next = rowProgress(r, liveProgress[r.id]);
      const prior = prev[r.id];
      // Cheap content signature: 10 state chars + isEnriching bit. If it
      // matches the previous rendered signature, keep the prior object
      // reference so downstream React.memo comparisons short-circuit.
      const sig =
        next.points.map((p) => p.state).join("") +
        (next.isEnriching ? "1" : "0");
      if (prior && prior.__sig === sig) {
        map[r.id] = prior;
      } else {
        next.__sig = sig;
        map[r.id] = next;
      }
    }
    prevProgressRef.current = map;
    return map;
  }, [realtors, liveProgress]);

  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const someChecked = selected.size > 0;
  const allChecked =
    realtors.length > 0 && realtors.every((r) => selected.has(r.id));

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      if (realtors.length > 0 && realtors.every((r) => prev.has(r.id))) {
        return new Set();
      }
      return new Set(realtors.map((r) => r.id));
    });
  }, [realtors]);

  const toggleOne = useCallback((id) => {
    setSelected((s) => {
      const c = new Set(s);
      if (c.has(id)) c.delete(id);
      else c.add(id);
      return c;
    });
  }, []);

  const downloadOne = useCallback(async (r) => {
    try {
      const token = localStorage.getItem(STORAGE_KEY);
      const url = `${API_BASE}/realtors/${r.id}/vcard`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      triggerDownload(blob, `${r.first_name || "realtor"}_${r.last_name || ""}.vcf`);
    } catch (e) {
      toast.error("Download failed", { description: String(e) });
    }
  }, []);

  const exportBulk = useCallback(async () => {
    try {
      const token = localStorage.getItem(STORAGE_KEY);
      const ids = someChecked ? [...selected] : undefined;
      const body = {
        ids,
        brokerage_id:
          !ids && brokerageFilter !== "all" ? brokerageFilter : undefined,
        search: !ids && debouncedSearch ? debouncedSearch : undefined,
        review_status: tab === "review" ? "pending" : undefined,
      };
      const resp = await fetch(`${API_BASE}/realtors/export-vcard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt);
      }
      const blob = await resp.blob();
      const name = ids ? `realtors_${ids.length}.vcf` : "realtors.vcf";
      triggerDownload(blob, name);
    } catch (e) {
      toast.error("Bulk export failed", { description: String(e) });
    }
  }, [selected, someChecked, brokerageFilter, debouncedSearch, tab]);

  const exportXlsx = useCallback(async () => {
    try {
      const token = localStorage.getItem(STORAGE_KEY);
      const ids = someChecked ? [...selected] : undefined;
      const body = {
        ids,
        brokerage_id:
          !ids && brokerageFilter !== "all" ? brokerageFilter : undefined,
        search: !ids && debouncedSearch ? debouncedSearch : undefined,
        review_status: tab === "review" ? "pending" : undefined,
      };
      const resp = await fetch(`${API_BASE}/realtors/export-xlsx`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt);
      }
      const blob = await resp.blob();
      const name = ids ? `realtors_${ids.length}.xlsx` : "realtors.xlsx";
      triggerDownload(blob, name);
      toast.success("Spreadsheet exported");
    } catch (e) {
      toast.error("Spreadsheet export failed", { description: String(e) });
    }
  }, [selected, someChecked, brokerageFilter, debouncedSearch, tab]);

  const enrich = useCallback(
    async (r) => {
      try {
        const { data } = await api.post(`/realtors/${r.id}/enrich`);
        trackJob(data.job_id, {
          type: "enrich_realtor",
          label: r.name,
        });
        toast.message(`Enriching ${r.name}…`, {
          description:
            "Public Google/DDG search for Facebook, Instagram, LinkedIn, TikTok.",
        });
      } catch (e) {
        toast.error("Couldn't enrich", { description: formatApiError(e) });
      }
    },
    [trackJob],
  );

  const [enrichAllOpen, setEnrichAllOpen] = useState(false);
  const [enrichAllMode, setEnrichAllMode] = useState("new_only");
  const [enrichAllCreditCap, setEnrichAllCreditCap] = useState(200);
  const [enrichAllEstimates, setEnrichAllEstimates] = useState(null);
  const [selectedEstimate, setSelectedEstimate] = useState(null);

  const openEnrichAll = useCallback(() => {
    const scoped = selectedIds.length > 0;
    setEnrichAllMode("new_only");
    setEnrichAllCreditCap(200);
    setEnrichAllEstimates(null);
    setSelectedEstimate(null);
    setEnrichAllOpen(true);
    (async () => {
      try {
        if (scoped) {
          // Only need one dry-run pass — the mode is ignored server-side
          // when `ids` is supplied, so any mode gives the same estimate.
          const { data } = await api.post(
            "/realtors/enrich-all",
            selectedIds,
            { params: { mode: "fill_gaps", dry_run: true } },
          );
          setSelectedEstimate(data);
          return;
        }
        const base =
          brokerageFilter !== "all" ? { brokerage_id: brokerageFilter } : {};
        const [a, b, c] = await Promise.all([
          api.post("/realtors/enrich-all", null, {
            params: { ...base, mode: "no_socials", dry_run: true },
          }),
          api.post("/realtors/enrich-all", null, {
            params: { ...base, mode: "fill_gaps", dry_run: true },
          }),
          api.post("/realtors/enrich-all", null, {
            params: { ...base, mode: "new_only", dry_run: true },
          }),
        ]);
        setEnrichAllEstimates({
          no_socials: a.data,
          fill_gaps: b.data,
          new_only: c.data,
        });
      } catch {
        // estimates are a nice-to-have; dialog still works without them
      }
    })();
  }, [brokerageFilter, selectedIds]);

  const enrichAll = useCallback(async () => {
    try {
      const scoped = selectedIds.length > 0;
      const params = {
        mode: enrichAllMode,
        credit_cap: enrichAllCreditCap,
      };
      if (!scoped && brokerageFilter !== "all")
        params.brokerage_id = brokerageFilter;
      const body = scoped ? selectedIds : null;
      const { data } = await api.post("/realtors/enrich-all", body, { params });
      trackJob(data.job_id, {
        type: "enrich_all",
        label: scoped ? `${data.targets} selected` : `${data.targets} realtors`,
      });
      toast.message(`Enriching ${data.targets} realtors…`, {
        description:
          enrichAllCreditCap > 0
            ? `Budget: ${enrichAllCreditCap} Firecrawl credits. Banner stays visible while it runs.`
            : "No credit cap — will use full budget. Banner stays visible while it runs.",
      });
      setEnrichAllOpen(false);
    } catch (e) {
      toast.error("Couldn't start", { description: formatApiError(e) });
    }
  }, [
    enrichAllMode,
    enrichAllCreditCap,
    brokerageFilter,
    selectedIds,
    trackJob,
  ]);

  const remove = useCallback(
    (r) => {
      setConfirmState({
        title: `Delete ${r.name}?`,
        description:
          "This permanently removes the realtor and their stored photo.",
        confirmLabel: "Delete realtor",
        destructive: true,
        onConfirm: async () => {
          setRealtors((arr) => arr.filter((x) => x.id !== r.id));
          try {
            await api.delete(`/realtors/${r.id}`);
            toast.success("Realtor deleted");
          } catch (e) {
            toast.error("Delete failed", { description: formatApiError(e) });
            reload();
          }
        },
      });
    },
    [reload],
  );

  const openEditor = useCallback((r) => setEditing(r), []);
  const openMerge = useCallback((r) => setMergeSource(r), []);
  const onMerged = useCallback((sourceId) => {
    setRealtors((arr) => arr.filter((r) => r.id !== sourceId));
    refreshReviewSummary();
  }, [refreshReviewSummary, setRealtors]);

  // In-place update on outreach touch — refresh JUST the affected realtor
  // via a targeted single-record GET, then patch the row. Prevents the
  // whole list from reloading (which used to blow away the user's scroll
  // position every time they verified a social profile).
  const onContactLogged = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/realtors/${id}`);
      patchRealtor(data);
    } catch (_) {
      // silent — the row keeps its slightly stale outreach_log until the
      // user navigates away and back; a full reload would be worse UX.
    }
  }, [patchRealtor]);

  // Fired after the "Is this the right profile?" confirm dialog is
  // accepted. Just patches verified_socials for the single row so the
  // dialog won't reappear next click; no network round-trip needed.
  const onSocialVerified = useCallback((id, urlField, entry) => {
    setRealtors((arr) =>
      arr.map((r) => {
        if (r.id !== id) return r;
        return {
          ...r,
          verified_socials: { ...(r.verified_socials || {}), [urlField]: entry },
        };
      }),
    );
  }, []);

  // Fired when the per-platform follow toggle is flipped (Mark as followed /
  // Mark as Unfollowed). Patches follows for the single row so the popover
  // button label + green check flip in place without a full list reload.
  const onFollowChanged = useCallback((id, key, ts) => {
    setRealtors((arr) =>
      arr.map((r) => {
        if (r.id !== id) return r;
        const follows = { ...(r.follows || {}) };
        if (ts) follows[key] = ts;
        else delete follows[key];
        return { ...r, follows };
      }),
    );
  }, []);

  // Bulk approve/reject on the Review tab. `action` is 'approve' or 'reject'.
  // Both go through the single `/realtors/review-bulk` endpoint. Empty
  // selection is a no-op — the button is disabled in that case.
  const runBulkReview = useCallback(async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setReviewBusy(true);
    try {
      const { data } = await api.post("/realtors/review-bulk", { ids, action });
      const past = action === "approve" ? "approved" : "rejected";
      toast.success(`${data.updated} realtor${data.updated === 1 ? "" : "s"} ${past}`);
      setSelected(new Set());
      await reload();
      refreshReviewSummary();
    } catch (e) {
      toast.error(`Bulk ${action} failed`, { description: formatApiError(e) });
    } finally {
      setReviewBusy(false);
    }
  }, [selected, reload, refreshReviewSummary]);

  // Single-row approve/reject from the row's inline buttons. Delegates
  // to the bulk endpoint via a 1-id list so error handling stays uniform.
  const approveOne = useCallback(async (id) => {
    try {
      await api.post(`/realtors/${id}/approve`);
      toast.success("Approved");
      await reload();
      refreshReviewSummary();
    } catch (e) {
      toast.error("Approve failed", { description: formatApiError(e) });
    }
  }, [reload, refreshReviewSummary]);
  const rejectOne = useCallback(async (id) => {
    try {
      await api.post(`/realtors/${id}/reject`);
      toast.success("Rejected");
      await reload();
      refreshReviewSummary();
    } catch (e) {
      toast.error("Reject failed", { description: formatApiError(e) });
    }
  }, [reload, refreshReviewSummary]);

  return (
    <div className="space-y-5" data-testid="realtors-page">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Step 2
          </p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-display">
            Realtors
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Search, edit, enrich, and export vCards.
            {someChecked ? (
              <span
                className="ml-2 inline-flex items-center rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-xs font-medium"
                data-testid="selection-count-chip"
              >
                {selected.size} selected — actions target this selection
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <RealtorsToolbar
            isAdmin={isAdmin}
            someChecked={someChecked}
            selectedCount={selected.size}
            selectedIds={selectedIds}
            hasRealtors={realtors.length > 0}
            enrichAllJob={enrichAllJob}
            onEnrichAll={openEnrichAll}
            onExport={exportBulk}
            onExportXlsx={exportXlsx}
            onAdd={() => setAddOpen(true)}
          />
        </div>
      </div>

      {/* Main / Review sub-tabs. Review is always visible; the count chip
          turns rose when new realtors from an open-house scan are waiting.
          Splitting the two lists prevents un-vetted names from polluting
          the curated main list. */}
      <div
        className="inline-flex rounded-md border border-slate-200 bg-white p-1 shadow-sm"
        role="tablist"
        aria-label="Realtors view"
        data-testid="realtors-tabs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === "main"}
          onClick={() => setTab("main")}
          className={
            "px-3 sm:px-4 py-2 text-sm font-medium rounded transition-colors " +
            (tab === "main"
              ? "bg-slate-900 text-white shadow-sm"
              : "text-slate-600 hover:text-slate-900")
          }
          data-testid="realtors-tab-main"
        >
          Main list
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "review"}
          onClick={() => setTab("review")}
          className={
            "px-3 sm:px-4 py-2 text-sm font-medium rounded transition-colors inline-flex items-center gap-2 " +
            (tab === "review"
              ? "bg-slate-900 text-white shadow-sm"
              : "text-slate-600 hover:text-slate-900")
          }
          data-testid="realtors-tab-review"
        >
          Review
          {reviewSummary.pending > 0 ? (
            <span
              className={
                "inline-flex items-center justify-center min-w-[1.25rem] h-5 rounded-full text-[11px] font-semibold px-1.5 " +
                (tab === "review"
                  ? "bg-white text-rose-700"
                  : "bg-rose-100 text-rose-700")
              }
              data-testid="realtors-review-count-chip"
            >
              {reviewSummary.pending}
            </span>
          ) : null}
        </button>
      </div>

      {/* Review tab helper banner — explains what this queue is for so a
          user who taps in without context understands. Also shows the
          bulk approve/reject actions inline so nothing needs a modal. */}
      {tab === "review" ? (
        <ReviewQueueBanner
          summary={reviewSummary}
          selected={selected}
          busy={reviewBusy}
          onApproveSelected={() => runBulkReview("approve")}
          onRejectSelected={() => runBulkReview("reject")}
        />
      ) : null}
      {/* Filters */}
      <RealtorsFilters
        search={search}
        onSearchChange={setSearch}
        brokerageFilter={brokerageFilter}
        onBrokerageFilterChange={changeBrokerageFilter}
        brokerages={brokerages}
        sort={sort}
        onSortChange={changeSort}
      />

      {/* Result count — "showing N of M". Anchor for A-Z jump scrolling. */}
      <div ref={listTopRef} className="scroll-mt-24" />
      {!loading && realtors.length > 0 ? (
        <div
          className="flex items-center justify-between text-xs text-slate-500 -mt-1"
          data-testid="realtors-result-count"
        >
          <span>
            Showing <strong className="text-slate-700 tabular-nums">{realtors.length}</strong>
            {" of "}
            <strong className="text-slate-700 tabular-nums">{total}</strong> realtor{total === 1 ? "" : "s"}
          </span>
          {letterJump?.ch ? (
            <button
              type="button"
              onClick={() => jumpToLetter(null, "#")}
              className="text-[hsl(var(--brand-strong))] hover:underline"
              data-testid="realtors-back-to-top"
            >
              Back to top
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Table on desktop, cards on mobile */}
      {loading ? (
        <div className="text-sm text-slate-500 p-6">Loading…</div>
      ) : realtors.length === 0 ? (
        <EmptyRealtors />
      ) : (
        <>
          {isMobile ? (
            /* Mobile cards — desktop table is intentionally NOT mounted here
               to keep iOS Safari under its per-tab memory ceiling. */
            <div className="flex items-stretch gap-1.5">
              <div className="flex-1 min-w-0">
                <ul className="space-y-3" data-testid="realtors-mobile-list">
                  {realtors.map((r) => (
                    <RealtorMobileCard
                      key={r.id}
                      realtor={r}
                      selected={selected.has(r.id)}
                      progress={progressById[r.id]}
                      enrichBusy={!!enrichJobs[r.id]}
                      onToggle={toggleOne}
                      onEnrich={enrich}
                      onDownload={downloadOne}
                      onDelete={remove}
                      onOpen={openEditor}
                      onPatch={patchRealtor}
                      onContactLogged={onContactLogged}
                      onSocialVerified={onSocialVerified}
                      onFollowChanged={onFollowChanged}
                      onBrokerageClick={setPeekBrokerageId}
                      platformOrder={channelOrder}
                    />
                  ))}
                </ul>
              </div>
              {/* Mobile A-Z rolodex — pinned to the right edge, sticky
                  vertically so it follows the user as they scroll. */}
              {showRolodex ? (
                <div className="shrink-0 self-start sticky top-2">
                  <AlphabetRolodex
                    letters={ROLODEX_LETTERS}
                    firstIdByLetter={rolodexFirstId}
                    onJump={jumpToLetter}
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <div className="flex items-stretch gap-2">
              {showRolodex ? (
                <div className="shrink-0 self-stretch">
                  <AlphabetRolodex
                    letters={ROLODEX_LETTERS}
                    firstIdByLetter={rolodexFirstId}
                    onJump={jumpToLetter}
                  />
                </div>
              ) : null}
              <div className="flex-1 min-w-0 overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table
                className="w-full text-sm data-table"
                style={{ tableLayout: "fixed" }}
              >
                <colgroup>
                  <col style={{ width: "44px", minWidth: "44px" }} />
                  {realtorCols.colGroup}
                </colgroup>
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left" style={{ width: "44px" }}>
                      <Checkbox
                        checked={allChecked}
                        onCheckedChange={toggleAll}
                        aria-label="Select all"
                      />
                    </th>
                    <SortableTH col={realtorCols.by("name")} sort={sort} onSort={changeSort} onBeginResize={realtorCols.beginDrag}>Name</SortableTH>
                    <SortableTH col={realtorCols.by("brokerage")} sort={sort} onSort={changeSort} onBeginResize={realtorCols.beginDrag} className="hidden lg:table-cell">Brokerage</SortableTH>
                    <SortableTH col={realtorCols.by("contact")} sort={sort} onSort={changeSort} onBeginResize={realtorCols.beginDrag}>Contact</SortableTH>
                    <SortableTH col={realtorCols.by("enrichment")} sort={sort} onSort={changeSort} onBeginResize={realtorCols.beginDrag}>Enrichment</SortableTH>
                    <SortableTH col={realtorCols.by("scan_count")} sort={sort} onSort={changeSort} onBeginResize={realtorCols.beginDrag} align="center" className="hidden md:table-cell">Scans</SortableTH>
                    <SortableTH col={realtorCols.by("socials")} sort={sort} onSort={changeSort} onBeginResize={realtorCols.beginDrag} className="hidden xl:table-cell">Socials</SortableTH>
                    <SortableTH col={realtorCols.by("actions")} sort={sort} onSort={changeSort} onBeginResize={realtorCols.beginDrag} align="right">Actions</SortableTH>
                  </tr>
                </thead>
                <tbody>
                  {realtors.map((r) => (
                    <RealtorRow
                      key={r.id}
                      realtor={r}
                      selected={selected.has(r.id)}
                      progress={progressById[r.id]}
                      enrichBusy={!!enrichJobs[r.id]}
                      onToggle={toggleOne}
                      onEnrich={enrich}
                      onDownload={downloadOne}
                      onDelete={remove}
                      onOpen={openEditor}
                      onPatch={patchRealtor}
                      onContactLogged={onContactLogged}
                      onSocialVerified={onSocialVerified}
                      onFollowChanged={onFollowChanged}
                      onApprove={approveOne}
                      onReject={rejectOne}
                      onMerge={tab === "review" ? openMerge : undefined}
                      onBrokerageClick={setPeekBrokerageId}
                      platformOrder={channelOrder}
                    />
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
          {/* Infinite-scroll sentinel + Load more fallback. */}
          <div ref={sentinelRef} aria-hidden="true" className="h-1" />
          {loadingMore ? (
            <div
              className="text-center text-xs text-slate-400 py-3"
              data-testid="realtors-loading-more"
            >
              Loading more…
            </div>
          ) : hasMore ? (
            <div className="flex justify-center py-3">
              <button
                type="button"
                onClick={loadMore}
                className="text-xs font-medium text-[hsl(var(--brand-strong))] hover:underline"
                data-testid="realtors-load-more"
              >
                Load more ({total - realtors.length} more)
              </button>
            </div>
          ) : null}
        </>
      )}

      <RealtorEditDialog
        realtor={editing}
        brokerages={brokerages}
        locations={locations}
        isAdmin={isAdmin}
        scrollTo={editing?.id === focusRealtorId ? scrollToSection : null}
        onClose={() => setEditing(null)}
        onPatch={patchRealtor}
        onDone={(updated) => {
          setEditing(null);
          // In-place update so the user's scroll position, filter and sort
          // are all preserved. `updated` is `{ id, ...patch }` from save or
          // `{ id, _deleted: true }` from delete. Falls back to a full
          // reload only if the child ever calls onDone() with no args.
          if (updated && updated.id && updated._deleted) {
            setRealtors((arr) => arr.filter((r) => r.id !== updated.id));
          } else if (updated && updated.id) {
            patchRealtor(updated);
          } else {
            reload();
          }
        }}
      />
      <AddRealtorManuallyDialog
        open={addOpen}
        brokerages={brokerages}
        onClose={() => setAddOpen(false)}
        onDone={() => {
          setAddOpen(false);
          reload();
        }}
      />
      <MergeRealtorDialog
        source={mergeSource}
        open={!!mergeSource}
        onClose={() => setMergeSource(null)}
        onMerged={onMerged}
      />
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel}
        destructive={confirmState?.destructive}
        onConfirm={confirmState?.onConfirm}
        onClose={() => setConfirmState(null)}
      />
      <EnrichAllDialog
        open={enrichAllOpen}
        onClose={() => setEnrichAllOpen(false)}
        onConfirm={enrichAll}
        mode={enrichAllMode}
        setMode={setEnrichAllMode}
        estimates={enrichAllEstimates}
        creditCap={enrichAllCreditCap}
        setCreditCap={setEnrichAllCreditCap}
        selectedCount={selectedIds.length}
        selectedEstimate={selectedEstimate}
      />

      {/* Brokerage peek — slide-in detail sheet from clicking a brokerage name. */}
      <BrokeragePeekModal
        brokerageId={peekBrokerageId}
        onClose={() => setPeekBrokerageId(null)}
      />
    </div>
  );
}